import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Headphones, Play, Pause, Loader2, Mic, BookOpen, RotateCcw, Share2, Download } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from "../ui/dialog";
import { getPodcast, generatePodcast, podcastAudioUrl } from "../../lib/adventureApi";

const PEOPLE = {
  raj: { name: "Raj", role: "Founder", img: "/podcast/raj.jpeg", color: "#E23D28" },
  simone: { name: "Simone", role: "Traveller", img: "/podcast/simone.jpeg", color: "#0E9E6E" },
};

function fmt(t) {
  if (!t || isNaN(t)) return "0:00";
  const m = Math.floor(t / 60), s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function Avatar({ who, speaking }) {
  const p = PEOPLE[who];
  return (
    <div className="flex flex-col items-center gap-2" data-testid={`podcast-avatar-${who}`}>
      <div className="relative">
        {speaking && (
          <>
            <motion.span className="absolute inset-0 rounded-full" style={{ boxShadow: `0 0 0 3px ${p.color}` }}
              animate={{ scale: [1, 1.18, 1], opacity: [0.7, 0, 0.7] }} transition={{ duration: 1.4, repeat: Infinity, ease: "easeOut" }} />
            <motion.span className="absolute inset-0 rounded-full" style={{ boxShadow: `0 0 0 2px ${p.color}` }}
              animate={{ scale: [1, 1.3, 1], opacity: [0.4, 0, 0.4] }} transition={{ duration: 1.4, repeat: Infinity, ease: "easeOut", delay: 0.3 }} />
          </>
        )}
        <motion.div
          animate={speaking ? { scale: [1, 1.03, 1] } : { scale: 1 }}
          transition={{ duration: 0.6, repeat: speaking ? Infinity : 0 }}
          className="relative rounded-full overflow-hidden border-4 transition-all duration-300"
          style={{ width: 116, height: 116, filter: speaking ? "none" : "saturate(0.75)", opacity: speaking ? 1 : 0.7, borderColor: p.color }}>
          <img src={p.img} alt={p.name} className="w-full h-full object-cover" />
        </motion.div>
        {/* talking equaliser */}
        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex items-end gap-[3px] h-5 px-2 py-1 rounded-full bg-ink/85 backdrop-blur"
          style={{ opacity: speaking ? 1 : 0 }}>
          {[0, 1, 2, 3].map((i) => (
            <motion.span key={i} className="w-[3px] rounded-full" style={{ background: p.color }}
              animate={speaking ? { height: [4, 12, 6, 14, 4] } : { height: 4 }}
              transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.12 }} />
          ))}
        </div>
      </div>
      <div className="text-center">
        <p className="font-display font-bold text-sm tracking-tight leading-none">{p.name}</p>
        <p className="font-mono text-[10px] uppercase tracking-widest text-ink-muted mt-1">{p.role}</p>
      </div>
    </div>
  );
}

function Studio({ slug, tourName }) {
  const [status, setStatus] = useState("checking"); // checking | idle | generating | ready | error
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState("");
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const [dur, setDur] = useState(0);
  const audioRef = useRef(null);
  const transcriptRef = useRef(null);
  const pollRef = useRef(null);

  const applyState = (d) => {
    if (d.exists || d.status === "ready") { setMeta(d); setStatus("ready"); return "ready"; }
    if (d.status === "generating") { setStatus("generating"); return "generating"; }
    if (d.status === "error") { setErr(d.error || "Could not create the episode."); setStatus("error"); return "error"; }
    setStatus("idle"); return "idle";
  };

  const startPolling = () => {
    clearInterval(pollRef.current);
    let tries = 0;
    pollRef.current = setInterval(async () => {
      tries += 1;
      try {
        const d = await getPodcast(slug);
        const s = applyState(d);
        if (s === "ready" || s === "error") clearInterval(pollRef.current);
      } catch { /* keep polling */ }
      if (tries > 60) { clearInterval(pollRef.current); setStatus("error"); setErr("This is taking longer than expected. Please try again."); }
    }, 3000);
  };

  useEffect(() => {
    let alive = true;
    getPodcast(slug)
      .then((d) => { if (!alive) return; const s = applyState(d); if (s === "generating") startPolling(); })
      .catch(() => alive && setStatus("idle"));
    return () => { alive = false; clearInterval(pollRef.current); };
  }, [slug]);

  const turns = meta?.turns || [];
  const activeIdx = useMemo(() => {
    let idx = -1;
    for (let i = 0; i < turns.length; i++) if (t + 0.05 >= turns[i].start) idx = i; else break;
    return idx;
  }, [t, turns]);
  const activeTurn = activeIdx >= 0 ? turns[activeIdx] : null;
  const speaker = activeTurn?.speaker || null;
  const isSpeaking = playing && activeTurn && t >= activeTurn.start && t <= activeTurn.end + 0.1;

  useEffect(() => {
    if (activeIdx < 0 || !transcriptRef.current) return;
    const el = transcriptRef.current.querySelector(`[data-turn="${activeIdx}"]`);
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeIdx]);

  const generate = () => {
    setStatus("generating"); setErr("");
    generatePodcast(slug)
      .then(() => startPolling())
      .catch((e) => { setErr(e?.response?.data?.detail || "Could not start generation."); setStatus("error"); });
  };

  const toggle = () => {
    const a = audioRef.current; if (!a) return;
    if (a.paused) {
      const p = a.play();
      if (p && p.then) {
        p.then(() => { setPlaying(true); setErr(""); })
         .catch(() => {
           setPlaying(false);
           setErr("Your browser blocked audio playback. If this keeps happening, it's usually a Chrome audio glitch — restart Chrome (or use the Download button to listen).");
         });
      } else {
        setPlaying(true);
      }
    } else {
      a.pause(); setPlaying(false);
    }
  };
  const seek = (e) => {
    const a = audioRef.current; if (!a || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    a.currentTime = ((e.clientX - rect.left) / rect.width) * dur;
    setT(a.currentTime);
  };
  const shareLink = () => {
    const url = `${window.location.origin}/tours/${slug}?podcast=1`;
    navigator.clipboard?.writeText(url).then(
      () => toast.success("Share link copied", { description: "Anyone who opens it hears this episode." }),
      () => toast.error("Could not copy the link"),
    );
  };

  return (
    <div className="flex flex-col" data-testid="podcast-studio">
      {/* Stage */}
      <div className="relative bg-gradient-to-b from-[#221c17] to-[#3a2f26] px-6 pt-8 pb-6">
        <div className="absolute top-3 left-4 flex items-center gap-1.5 text-white/60 font-mono text-[10px] uppercase tracking-widest">
          <Mic className="w-3.5 h-3.5" /> Responsible Adventures · What to expect
        </div>
        <div className="flex items-center justify-center gap-10 sm:gap-16 mt-4">
          <Avatar who="raj" speaking={isSpeaking && speaker === "raj"} />
          <Avatar who="simone" speaking={isSpeaking && speaker === "simone"} />
        </div>
        {/* live caption */}
        <div className="mt-6 min-h-[52px] max-w-xl mx-auto text-center">
          <AnimatePresence mode="wait">
            {status === "ready" && activeTurn ? (
              <motion.p key={activeIdx} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="text-white/90 text-sm leading-relaxed" data-testid="podcast-caption">
                <span className="font-semibold" style={{ color: PEOPLE[speaker]?.color }}>{PEOPLE[speaker]?.name}: </span>
                {activeTurn.text}
              </motion.p>
            ) : (
              <p className="text-white/50 text-sm">{tourName}</p>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Controls / states */}
      <div className="bg-[#FDFBF7] px-6 py-5">
        {status === "checking" && (
          <div className="flex items-center justify-center gap-2 text-ink-muted text-sm py-3"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        )}

        {status === "idle" && (
          <div className="text-center py-2" data-testid="podcast-idle">
            <p className="text-sm text-ink-secondary max-w-md mx-auto">A short podcast where <b>Raj</b> and <b>Simone</b> talk through what to expect — drawn from our own travel journal.</p>
            <button onClick={generate} data-testid="podcast-generate-btn"
              className="mt-4 inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white h-11 px-6 rounded-sm font-semibold tracking-wide transition-colors">
              <Headphones className="w-4 h-4" /> Create the episode
            </button>
            <p className="text-[11px] text-ink-muted mt-2">Takes ~30–60s the first time, then it's saved.</p>
          </div>
        )}

        {status === "generating" && (
          <div className="text-center py-4" data-testid="podcast-generating">
            <Loader2 className="w-6 h-6 animate-spin text-terracotta mx-auto" />
            <p className="text-sm text-ink-secondary mt-3">Recording the episode with Raj &amp; Simone…</p>
            <p className="text-[11px] text-ink-muted mt-1">Writing the script from our journal, then voicing it. This happens only once.</p>
          </div>
        )}

        {status === "error" && (
          <div className="text-center py-3" data-testid="podcast-error">
            <p className="text-sm text-terracotta">{err}</p>
            <button onClick={generate} className="mt-3 inline-flex items-center gap-2 border border-sand-dark hover:border-moss h-10 px-4 rounded-sm text-sm font-medium transition-colors">
              <RotateCcw className="w-4 h-4" /> Try again
            </button>
          </div>
        )}

        {status === "ready" && meta && (
          <div data-testid="podcast-ready">
            <audio ref={audioRef} src={podcastAudioUrl(slug)} preload="metadata"
              onLoadedMetadata={(e) => setDur(e.currentTarget.duration || meta.duration || 0)}
              onTimeUpdate={(e) => setT(e.currentTarget.currentTime)}
              onError={() => setErr("This audio couldn't play in your browser. Tap Download to listen, or restart Chrome if it keeps happening.")}
              onPlaying={() => { setPlaying(true); setErr(""); }}
              onPause={() => setPlaying(false)}
              onEnded={() => setPlaying(false)} />
            <div className="flex items-center gap-4">
              <button onClick={toggle} data-testid="podcast-play-btn"
                className="w-12 h-12 shrink-0 rounded-full bg-terracotta hover:bg-terracotta-hover text-white flex items-center justify-center transition-colors">
                {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
              </button>
              <div className="flex-1">
                <div onClick={seek} className="h-2 rounded-full bg-sand-dark cursor-pointer overflow-hidden" data-testid="podcast-progress">
                  <div className="h-full bg-terracotta rounded-full" style={{ width: `${dur ? (t / dur) * 100 : 0}%` }} />
                </div>
                <div className="flex justify-between font-mono text-[11px] text-ink-muted mt-1.5">
                  <span>{fmt(t)}</span><span>{fmt(dur || meta.duration)}</span>
                </div>
              </div>
            </div>

            {err && <p className="text-xs text-terracotta mt-2" data-testid="podcast-play-error">{err}</p>}

            {/* share + download */}
            <div className="flex items-center gap-2 mt-3">
              <button onClick={shareLink} data-testid="podcast-share-btn"
                className="flex-1 flex items-center justify-center gap-2 border border-sand-dark hover:border-moss h-9 rounded-sm text-xs font-medium transition-colors">
                <Share2 className="w-3.5 h-3.5 text-moss" /> Share link
              </button>
              <a href={`${podcastAudioUrl(slug)}?download=1`} download={`${slug}-podcast.mp3`} data-testid="podcast-download-btn"
                className="flex-1 flex items-center justify-center gap-2 border border-sand-dark hover:border-moss h-9 rounded-sm text-xs font-medium transition-colors">
                <Download className="w-3.5 h-3.5 text-moss" /> Download
              </a>
            </div>

            {/* full transcript */}
            <div ref={transcriptRef} className="mt-4 max-h-44 overflow-y-auto thin-scroll border-t border-sand-dark pt-3 space-y-2" data-testid="podcast-transcript">
              {turns.map((tn, i) => (
                <button key={i} data-turn={i} onClick={() => { if (audioRef.current) { audioRef.current.currentTime = tn.start; setT(tn.start); if (!playing) toggle(); } }}
                  className={`w-full text-left flex gap-2.5 text-sm rounded-sm px-2 py-1.5 transition-colors ${i === activeIdx ? "bg-white" : "hover:bg-white/60 opacity-70"}`}>
                  <span className="font-semibold shrink-0" style={{ color: PEOPLE[tn.speaker]?.color }}>{PEOPLE[tn.speaker]?.name}</span>
                  <span className="text-ink-secondary leading-snug">{tn.text}</span>
                </button>
              ))}
            </div>

            {meta.sources?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-sand-dark">
                <p className="font-mono text-[10px] uppercase tracking-widest text-moss flex items-center gap-1.5 mb-1.5"><BookOpen className="w-3.5 h-3.5" /> From our journal</p>
                <div className="flex flex-wrap gap-2">
                  {meta.sources.map((s) => (
                    <a key={s.url} href={s.url} target="_blank" rel="noreferrer"
                      className="text-[11px] text-ink-secondary bg-moss-light hover:text-moss px-2.5 py-1 rounded-full transition-colors">{s.title}</a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function PodcastPlayer({ slug, tourName, triggerClassName, triggerLabel = "Listen: What to expect", defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => { if (defaultOpen) setOpen(true); }, [defaultOpen]);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="podcast-open-btn" className={triggerClassName}>
          <Headphones className="w-4 h-4" /> {triggerLabel}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg w-[94vw] p-0 gap-0 overflow-hidden rounded-md" data-testid="podcast-modal">
        <DialogTitle className="sr-only">What to expect podcast</DialogTitle>
        {open && <Studio slug={slug} tourName={tourName} />}
      </DialogContent>
    </Dialog>
  );
}
